/**
 * @module app.arrowstyle
 */
import {HIGHLIGHT_SPACING_SMALL, HIGHLIGHT_SPACING} from './constants.js';
import {mapProjectionToLonLat, addBuffer} from './utils.js';
import {containsCoordinate} from 'ol/extent.js';
import olGeomMultiLineString from 'ol/geom/MultiLineString.js';
import olGeomPoint from 'ol/geom/Point.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleRegularShape from 'ol/style/RegularShape.js';
import olStyleStyle from 'ol/style/Style.js';

/**
 * @const
 */
export const ARROW_ICON_PATH = 'img/highlight/arrow.svg';

/**
 * @const
 */
export const ARROW_ICON_PATH_SMALL = 'img/highlight/arrow_small.svg';

/**
 * @const
 */
export const ARROW_ICON_SIZE = [16, 16];

/**
 * @const
 */
export const ARROW_3D_COLOR = [229, 30, 30, 1.0];

/**
 * @const
 */
export const ARROW_3D_INTERVAL_METERS = 100;

/**
 * @const
 */
export const ARROW_POLYGON = [
  [-8, -7],
  [8, 0],
  [-8, 7]
];

/**
 * @const
 */
export const EMPTY_IMAGE = new olStyleRegularShape({points: 0});

export const ArrowStyle = class {
  /**
   * @param {ol.Map} map
   */
  constructor(map) {
    /**
     * @private
     */
    this.map_ = map;

    /**
     * @private
     * @const {!Object<string, !ol.style.Icon>}
     */
    this.iconCache_ = {};
  }

  /**
   * Given a MultiLineString feature, create a style array that contains oriented
   * arrows along all of its line strings. For a big feature at the highest
   * zoom level, the number of arrows could be in the thousands. The generation
   * is therefore limited to the map / print extent.
   *
   * @param {ol.Feature} feature
   * @param {number} resolution
   * @return {!Array.<ol.style.Style>}
   */
  create2DArrowStyles(feature, resolution) {
    let extent = this.map_.getView().calculateExtent();
    if (this.isPrinting_()) {
      extent = /** @type {ol.Extent} */ (this.map_.getProperties()['printExtent']);
    }
    extent = this.addBuffer_(extent, resolution);

    const isSmallResolution = (resolution <= 20);
    const arrowPixelInterval = isSmallResolution ? HIGHLIGHT_SPACING_SMALL : HIGHLIGHT_SPACING;
    const arrowInterval = resolution * arrowPixelInterval;
    const arrowFactory = (arrowIndex, position, rotation) => {
      return this.create2DArrowStyle_(position, rotation, isSmallResolution);
    };

    return accumulateStylesForFeature_(feature, (lineString) => {
      return this.lineStringToArrows_(lineString, arrowInterval, arrowFactory, extent);
    });
  }

  /**
   * Create arrows every 100 m along the feature geometry.
   *
   * @param {ol.Feature} feature
   * @return {!Array.<ol.style.Style>}
   */
  create3DArrowStyles(feature) {
    const arrowInterval = ARROW_3D_INTERVAL_METERS;
    const arrowFactory = this.create3DArrowStyle_.bind(this);
    return accumulateStylesForFeature_(feature, (lineString) => {
      return this.lineStringToArrows_(lineString, arrowInterval, arrowFactory);
    });
  }

  /**
   * @private
   * @return {boolean}
   */
  isPrinting_() {
    return !!this.map_.getProperties()['isPrinting'];
  }

  /**
   * Add buffer to extent to ensure that icons near the view boundary are visible
   * @private
   * @param {ol.Extent} extent
   * @param {number} resolution
   * @return {ol.Extent}
   */
  addBuffer_(extent, resolution) {
    const buffer = Math.max(...ARROW_ICON_SIZE);
    return addBuffer(extent, resolution, buffer);
  }

  /**
   * @private
   * @param {ol.geom.LineString} lineString
   * @param {number} arrowInterval
   * @param {function(number, !ol.Coordinate, number): ol.style.Style} arrowFactory
   * @param {ol.Extent=} extent
   * @return {Array.<ol.style.Style>}
   */
  lineStringToArrows_(lineString, arrowInterval, arrowFactory, extent) {
    const pointInterval = arrowInterval / 2;
    const length = lineString.getLength();
    const nPoints = Math.floor(length / pointInterval) + 1;
    const nArrows = Math.floor(nPoints / 2);
    const styles = [];
    for (let i = 0; i < nArrows; i++) {
      const start = lineString.getCoordinateAt((2 * i) * pointInterval / length);
      if (!extent || containsCoordinate(extent, start)) {
        const end = lineString.getCoordinateAt((2 * i + 1) * pointInterval / length);
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const rotation = -Math.atan2(dy, dx);
        styles.push(arrowFactory(i, start, rotation));
      }
    }
    return styles;
  }

  /**
   * @private
   * @param {!ol.Coordinate} position
   * @param {number} rotation
   * @param {boolean} isSmallResolution
   * @return {ol.style.Style}
   */
  create2DArrowStyle_(position, rotation, isSmallResolution) {
    return new olStyleStyle({
      geometry: new olGeomPoint(position),
      image: this.getArrowIconForRotation_(rotation, isSmallResolution)
    });
  }

  /**
   * @private
   * @param {number} arrowIndex
   * @param {!ol.Coordinate} position
   * @param {number} rotation
   */
  create3DArrowStyle_(arrowIndex, position, rotation) {
    const point = new olGeomPoint(position);

    const level = countTrailingZeroes_(arrowIndex + 1);

    const arrowPolygonsLatLng = [];

    // Create arrows of different sizes for different zoom levels
    for (let i = 0; i <= level; i++) {
      const scale = Math.pow(2, i);
      const arrowPolygonLatLng = make3DArrow_(position, scale, rotation);
      arrowPolygonsLatLng.push(arrowPolygonLatLng);
    }

    point.set('olcsCustomPoint', {
      'color': ARROW_3D_COLOR,
      'level': level,
      'polygonsLatLng': arrowPolygonsLatLng
    });

    return new olStyleStyle({
      geometry: point,
      image: EMPTY_IMAGE
    });
  }

  /**
   * @private
   * @param {number} rotation
   * @param {boolean} isSmallResolution
   * @return {!ol.style.Icon}
   */
  getArrowIconForRotation_(rotation, isSmallResolution) {
    // Snap to 100 distinct rotation values
    const rotationFactor = 100 / (2 * Math.PI);
    const iconPath = isSmallResolution ? ARROW_ICON_PATH : ARROW_ICON_PATH_SMALL;
    const rotationKey = Math.round(rotation * rotationFactor);
    const cacheKey = `${iconPath}-${rotationKey}`;
    if (!this.iconCache_[cacheKey]) {
      const roundedRotation = rotationKey / rotationFactor;
      this.iconCache_[cacheKey] = new olStyleIcon({
        imgSize: ARROW_ICON_SIZE,
        src: iconPath,
        rotation: roundedRotation
      });
    }
    return this.iconCache_[cacheKey];
  }
};

/**
 * Count the trailing zeroes in the binary representation of value.
 * Another way of stating this is: return the position of the least
 * significant bit that is set.
 * This is useful for determining the zoom level of visibility for an arrow, such
 * that at every zoom level increase, the number of visible arrows is doubled.
 *
 * Adapted from: https://graphics.stanford.edu/~seander/bithacks.html#ZerosOnRightParallel
 *
 * @private
 * @param {number} value
 */
export function countTrailingZeroes_(value) {
  let count = 32;
  value &= -value;
  if (value) {
    count--;
  }
  if (value & 0x0000FFFF) {
    count -= 16;
  }
  if (value & 0x00FF00FF) {
    count -= 8;
  }
  if (value & 0x0F0F0F0F) {
    count -= 4;
  }
  if (value & 0x33333333) {
    count -= 2;
  }
  if (value & 0x55555555) {
    count -= 1;
  }
  return count;
}

/**
 * @private
 * @param {ol.Feature} feature
 * @param {function(ol.geom.LineString)} func
 * @return {!Array.<ol.style.Style>}
 */
export function accumulateStylesForFeature_(feature, func) {
  let styles = [];
  const geometry = feature.getGeometry();
  if (geometry instanceof olGeomMultiLineString) {
    geometry.getLineStrings().forEach((lineString) => {
      const stylesForLineString = func(lineString);
      styles = styles.concat(stylesForLineString);
    });
  }
  return styles;
}

/**
 * @private
 * @param {!Array.<number>} point
 * @param {number} angle
 * @return {!Array.<number>}
 */
export function rotatePoint_(point, angle) {
  const x = point[0];
  const y = point[1];
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);
  return [x * cos - y * sin, y * cos + x * sin];
}

/**
 * @private
 * @param {!Array.<number>} point
 * @param {!Array.<number>} offset
 * @return {!Array.<number>}
 */
export function offsetPoint_(point, offset) {
  return point.map((coordinate, i) => coordinate + offset[i]);
}

/**
 * @private
 * @param {!Array.<number>} point
 * @param {number} scale
 * @return {!Array.<number>}
 */
export function scalePoint_(point, scale) {
  return point.map(coordinate => coordinate * scale);
}

/**
 * @private
 * @param {!Array.<number>} position
 * @param {number} scale
 * @param {number} rotation
 * @return {!Array.<!Array.<number>>}
 */
export function make3DArrow_(position, scale, rotation) {
  return ARROW_POLYGON.map((point) => {
    const withRotation = rotatePoint_(point, -rotation);
    const withScale = scalePoint_(withRotation, scale);
    const withOffset = offsetPoint_(withScale, position);
    const latLng = mapProjectionToLonLat(withOffset);
    return latLng;
  });
}
